// <v12-finance-calculator> — V12 Retail Finance on the product page, styled after the
// Berry's (Novuna) finance options. Markup and strings: snippets/v12-finance-calculator.liquid.
//
// The plan cards arrive server-rendered (snippets/v12-plan-quote.liquid), so nothing here
// runs on the critical path. V12's CalculatorScript (window.V12: the retailer's plans and
// V12.calculate) is served with `max-age=0`, so it is injected only on interaction — the
// pointer or focus reaching the component, opening the dialog — or a variant change that
// needs the cards recalculated. It is fetched once per page.

const ANNOUNCE_DELAY_MS = 750;

const scriptLoads = new Map();

function loadV12(src) {
  if (window.V12?.calculate) return Promise.resolve(window.V12);
  if (!scriptLoads.has(src)) {
    const load = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () =>
        window.V12?.calculate ? resolve(window.V12) : reject(new Error('V12 calculator script did not initialise'));
      script.onerror = () => reject(new Error('V12 calculator script failed to load'));
      document.head.append(script);
    });
    // Allow a retry after a failure (e.g. a flaky connection).
    load.catch(() => scriptLoads.delete(src));
    scriptLoads.set(src, load);
  }
  return scriptLoads.get(src);
}

// Fill "[name]" placeholders. Square brackets so Liquid's `t` filter leaves them alone.
function format(template = '', values = {}) {
  return template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
}

const planKey = (product) => `${Number(product.apr)}:${product.months}`;

class V12FinanceCalculator extends HTMLElement {
  #abort;
  #v12 = null;
  #frame = 0;
  #announceTimer = 0;
  #returnFocus = null;
  #returnFocusOnClose = true;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const on = (target, type, listener) => target.addEventListener(type, listener, { signal });

    this.strings = JSON.parse(this.querySelector('[data-v12-strings]')?.textContent || '{}');
    this.variantPrices = JSON.parse(this.querySelector('[data-v12-variants]')?.textContent || '{}');
    this.variantAvailability = JSON.parse(this.querySelector('[data-v12-availability]')?.textContent || '{}');
    this.minLoan = Number(this.dataset.minLoan) / 100;
    this.minDepositPercent = Number(this.dataset.minDepositPercent) || 0;
    this.maxLoan = Number(this.dataset.maxLoan) / 100;
    this.cashPrice = Number(this.dataset.price) / 100;
    this.scope = this.closest('.shopify-section') || document;

    const locale = this.dataset.locale || 'en-GB';
    this.currency = new Intl.NumberFormat(locale, { style: 'currency', currency: 'GBP' });
    this.percent = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });

    this.plans = this.querySelector('[data-v12-mode="plans"]');
    this.planButtons = [...this.querySelectorAll('[data-v12-plan]')];
    this.dialog = this.querySelector('dialog');
    this.deposit = this.querySelector('[data-v12-deposit]');
    this.term = this.querySelector('[data-v12-term]');
    this.depositHelp = this.querySelector('[data-v12-deposit-help]');
    this.depositError = this.querySelector('[data-v12-deposit-error]');
    this.calculation = this.querySelector('[data-v12-calculation]');
    this.calculationStatus = this.querySelector('[data-v12-calculation-status]');
    this.loadingText = this.calculationStatus.textContent;
    this.quote = this.querySelector('[data-v12-quote]');
    this.purchase = this.querySelector('[data-v12-purchase]');
    this.status = this.querySelector('[data-v12-status]');

    // Warm V12 up on the first sign of intent, so the dialog usually opens fully ready.
    on(this, 'pointerenter', () => this.#load());
    on(this, 'focusin', () => this.#load());

    for (const button of this.planButtons) on(button, 'click', () => this.#open(button));
    on(this.querySelector('[data-v12-close]'), 'click', () => this.dialog.close());
    on(this.dialog, 'close', () => {
      if (this.#returnFocusOnClose) this.#returnFocus?.focus({ preventScroll: true });
    });
    // Close on a click outside the dialog box (on the backdrop).
    on(this.dialog, 'click', (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= box.left &&
        event.clientX <= box.right &&
        event.clientY >= box.top &&
        event.clientY <= box.bottom;
      if (!inside) this.dialog.close();
    });
    on(this.deposit, 'input', () => this.#schedule());
    on(this.term, 'change', () => this.#update());
    on(this.purchase, 'click', () => this.#addToBag());

    if (this.dataset.form) {
      // The BR26 size picker switches variant by changing the form's `id` field and
      // re-fetching parts of the section; it never fires variant:change. Follow the
      // field itself (as berrys-product-finance does) and look the price up.
      on(document, 'change', (event) => {
        const field = event.target;
        if (!field.matches?.('input[name="id"], select[name="id"]')) return;
        if (field.form?.getAttribute('id') !== this.dataset.form) return;
        const price = this.variantPrices[field.value];
        if (price !== undefined) this.#setPrice(price, this.variantAvailability[field.value] === true);
      });
      // Impact's own pickers dispatch variant:change on the product form; it bubbles.
      on(document, 'variant:change', (event) => {
        // getAttribute, not .id: a product form's <input name="id"> shadows form.id.
        if (event.target?.getAttribute?.('id') !== this.dataset.form) return;
        const price = event.detail?.variant?.price;
        if (price !== undefined) this.#setPrice(price, event.detail?.variant?.available === true);
      });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#announceTimer);
  }

  /** Resolves to window.V12, or null when the script can't be loaded. */
  async #load() {
    if (this.dataset.personalOnly === 'true') return null;
    if (this.#v12) return this.#v12;
    try {
      this.#v12 = await loadV12(this.dataset.scriptSrc);
      return this.#v12;
    } catch (error) {
      console.warn('[v12-finance-calculator]', error);
      return null;
    }
  }

  /** Every V12 plan (0% and 9.9%), whichever card opened the dialog; that card's plan is preselected. */
  #populateTerms() {
    const options = this.#v12
      .getFinanceProducts()
      .map(
        (product) =>
          new Option(
            format(this.strings.termOption, { months: product.months, apr: Number(product.apr) }),
            planKey(product),
          ),
      );
    this.term.replaceChildren(...options);
  }

  #product(key) {
    return this.#v12?.getFinanceProducts().find((product) => planKey(product) === key) ?? null;
  }

  // V12.calculate throws for plans with a document fee (they need moment.js, which V12
  // bundles separately); treat those as unavailable rather than breaking the page.
  #calculate(product, deposit) {
    try {
      return this.#v12.calculate(product, this.cashPrice, deposit);
    } catch (error) {
      console.warn('[v12-finance-calculator]', error);
      return null;
    }
  }

  /** Theme settings' minimum deposit (%) of the price, rounded up to the penny (as in Liquid). */
  get #minDeposit() {
    return Math.ceil((Math.round(this.cashPrice * 100) * this.minDepositPercent) / 100) / 100;
  }

  get #maxDeposit() {
    return Math.max(0, Math.round((this.cashPrice - this.minLoan) * 100) / 100);
  }

  // ----- Variant changes -------------------------------------------------------------

  async #setPrice(pence, available) {
    this.cashPrice = pence / 100;
    this.dataset.price = String(pence);
    if (this.dialog.open) {
      this.#returnFocusOnClose = false;
      this.dialog.close();
      this.#returnFocusOnClose = true;
    }

    if (this.dataset.personalOnly === 'true') {
      this.hidden = !available || this.dataset.highJewellery !== 'true' || this.cashPrice <= this.maxLoan;
      this.#setMode('personal');
      return;
    }

    // Hidden when even the minimum deposit leaves a loan under V12's minimum.
    this.hidden = !available || this.cashPrice - this.#minDeposit < this.minLoan;
    if (this.cashPrice > this.maxLoan) {
      this.#setMode(this.dataset.highJewellery === 'true' ? 'personal' : 'contact');
      return;
    }
    this.#setMode('plans');
    if (this.hidden) return;

    // The server-rendered figures belong to the previous variant until V12 recalculates.
    this.plans.setAttribute('aria-busy', 'true');
    const v12 = await this.#load();
    this.plans.removeAttribute('aria-busy');
    if (!v12) {
      this.#setMode('contact');
      return;
    }
    if (!this.#renderPlans()) this.#setMode('contact');
  }

  #setMode(mode) {
    this.dataset.mode = mode;
    for (const block of this.querySelectorAll('[data-v12-mode]')) block.hidden = block.dataset.v12Mode !== mode;
  }

  /** Lowest available plan per APR group at the minimum deposit. Returns whether any card shows. */
  #renderPlans() {
    let visible = 0;
    for (const button of this.planButtons) {
      const apr = Number(button.dataset.v12PlanApr);
      let best = null;
      for (const product of this.#v12.getFinanceProducts()) {
        if (Number(product.apr) !== apr) continue;
        const payments = this.#calculate(product, this.#minDeposit);
        if (!payments?.productAvailable) continue;
        if (!best || Number(payments.initialPayments) < Number(best.payments.initialPayments))
          best = { product, payments };
      }
      button.hidden = !best;
      button.dataset.v12Plan = best ? planKey(best.product) : '';
      if (!best) continue;
      visible++;
      button.querySelector('[data-v12-plan-amount]').textContent = this.#money(best.payments.initialPayments);
      button.querySelector('[data-v12-plan-term]').textContent = format(this.strings.planTerm, {
        months: best.product.months,
      });
      button.querySelector('[data-v12-plan-deposit]').textContent =
        this.#minDeposit > 0
          ? format(this.strings.planDeposit, {
              percent: this.percent.format(this.minDepositPercent),
              amount: this.#money(this.#minDeposit),
            })
          : this.strings.noDeposit;
    }
    return visible > 0;
  }

  // ----- Dialog ----------------------------------------------------------------------

  async #open(button) {
    if (this.dataset.personalOnly === 'true') return;
    const key = button.dataset.v12Plan;
    if (!key) return;
    this.#returnFocus = button;
    this.deposit.value = this.#minDeposit.toFixed(2);
    this.deposit.min = this.#minDeposit.toFixed(2);
    this.deposit.max = this.#maxDeposit.toFixed(2);
    this.depositHelp.textContent = format(this.strings.depositHelp, {
      min: this.#money(this.#minDeposit),
      max: this.#money(this.#maxDeposit),
    });
    this.#setInvalid(false);
    this.calculation.hidden = false;
    this.quote.hidden = true;
    this.calculationStatus.hidden = false;
    this.calculationStatus.textContent = this.loadingText;
    this.#syncPurchase(false);
    this.dialog.showModal();
    this.deposit.focus();

    const v12 = await this.#load();
    if (!this.dialog.open) return;
    if (!v12) {
      this.calculationStatus.textContent = this.strings.loadError;
      return;
    }
    this.#populateTerms();
    this.term.value = key;
    this.#update({ announce: false });
  }

  // Coalesce rapid typing into one calculation per frame (INP).
  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  #readDeposit() {
    const raw = this.deposit.value.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
    const deposit = Number(raw);
    // Compare in pence so 99.75 vs 99.75000001 can't misjudge the bounds.
    const pence = Math.round(deposit * 100);
    const inRange = pence >= Math.round(this.#minDeposit * 100) && pence <= Math.round(this.#maxDeposit * 100);
    return inRange ? deposit : null;
  }

  #setInvalid(invalid) {
    this.depositError.hidden = !invalid;
    if (invalid) {
      this.deposit.setAttribute('aria-invalid', 'true');
      this.depositError.textContent = format(this.strings.depositError, {
        min: this.#money(this.#minDeposit),
        max: this.#money(this.#maxDeposit),
      });
    } else {
      this.deposit.removeAttribute('aria-invalid');
    }
  }

  #update({ announce = true } = {}) {
    if (!this.#v12 || !this.dialog.open) return;
    const deposit = this.#readDeposit();
    const product = this.#product(this.term.value);
    const payments = deposit !== null && product ? this.#calculate(product, deposit) : null;
    const valid = Boolean(payments?.productAvailable);

    // An invalid deposit hides the figures entirely (as the Novuna dialog does); a plan
    // V12 can't calculate shows the load error in their place.
    this.#setInvalid(deposit === null);
    this.calculation.hidden = deposit === null;
    this.quote.hidden = !valid;
    this.calculationStatus.hidden = valid;
    if (!valid) this.calculationStatus.textContent = this.strings.loadError;
    this.#syncPurchase(valid);
    if (!valid) return;

    this.#renderQuote(product, payments);
    if (announce) this.#announce(product, payments);
  }

  #renderQuote(product, payments) {
    const money = (value) => this.#money(value);
    const apr = `${this.percent.format(Number(product.apr))}%`;
    const deposit = Number(payments.deposit);
    const depositText =
      deposit > 0
        ? format(this.strings.depositPercent, {
            percent: this.percent.format((deposit / Number(payments.cashPrice)) * 100),
          })
        : this.strings.noDeposit;

    const hasFinal = payments.initialPayments !== payments.finalPayment && Number(payments.finalPayment) > 0;
    const example = [
      format(this.strings.exampleCashDeposit, { price: money(payments.cashPrice), deposit: money(deposit) }),
      format(this.strings.exampleCredit, { credit: money(payments.loanAmount) }),
      hasFinal
        ? format(this.strings.examplePaymentsFinal, {
            regular: payments.months - 1,
            monthly: money(payments.initialPayments),
            final: money(payments.finalPayment),
          })
        : format(this.strings.examplePayments, { months: payments.months, monthly: money(payments.initialPayments) }),
      format(this.strings.exampleTotal, { total: money(payments.amountPayable) }),
      format(this.strings.exampleCost, { cost: money(payments.chargeForCredit) }),
      format(this.strings.exampleInterest, { rate: this.percent.format(Number(payments.annualRate)) }),
      format(this.strings.exampleApr, { apr }),
    ].join(' ');

    const fields = {
      monthly: money(payments.initialPayments),
      apr,
      basis: format(this.strings.basis, { deposit: depositText, months: payments.months }),
      example,
    };
    for (const [name, value] of Object.entries(fields)) {
      this.querySelector(`[data-v12-field="${name}"]`).textContent = value;
    }
  }

  // One polite announcement once input settles, not one per keystroke.
  #announce(product, payments) {
    clearTimeout(this.#announceTimer);
    this.#announceTimer = setTimeout(() => {
      this.status.textContent = format(this.strings.statusSummary, {
        amount: this.#money(payments.initialPayments),
        months: payments.months,
        apr: `${this.percent.format(Number(product.apr))}%`,
      });
    }, ANNOUNCE_DELAY_MS);
  }

  // ----- Add to bag (mirrors berrys-product-finance) ----------------------------------

  #purchaseTarget() {
    return this.scope.querySelector(
      'product-actions-form form[data-type="add-to-cart-form"] button[name="add"][type="submit"]',
    );
  }

  #syncPurchase(validQuote) {
    const target = this.#purchaseTarget();
    this.purchase.hidden = !target;
    if (!target) return;
    const optionsRequired = target.hasAttribute('data-select-option');
    const targetUnavailable =
      target.disabled || target.getAttribute('aria-disabled') === 'true' || getComputedStyle(target).display === 'none';
    this.purchase.disabled = !validQuote || (!optionsRequired && targetUnavailable);
    this.purchase.textContent = optionsRequired
      ? this.purchase.dataset.optionsLabel
      : target.querySelector('span')?.textContent.trim() || this.purchase.dataset.defaultLabel;
  }

  #addToBag() {
    const target = this.#purchaseTarget();
    const form = target?.form;
    if (!this.dialog.open || !form || this.purchase.disabled) return;
    this.#returnFocusOnClose = false;
    this.dialog.close();
    this.#returnFocusOnClose = true;

    requestAnimationFrame(() => {
      if (target.hasAttribute('data-select-option')) {
        const options = this.scope.querySelector('variant-radios');
        const control = [
          ...(options?.querySelectorAll('button, select, input:not([type="hidden"]):not([disabled]), [tabindex]') ??
            []),
        ].find((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        options?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        control?.focus({ preventScroll: true });
        return;
      }
      if (target.disabled || target.getAttribute('aria-disabled') === 'true') return;
      if (!form.reportValidity()) return;
      target.focus({ preventScroll: true });
      target.click();
      const consent = form.querySelector('#wrings-crafting-consent');
      if (consent && !consent.checked) {
        consent.scrollIntoView({ block: 'center' });
        consent.focus({ preventScroll: true });
      }
    });
  }

  #money(value) {
    return this.currency.format(Number(value));
  }
}

if (!customElements.get('v12-finance-calculator')) {
  customElements.define('v12-finance-calculator', V12FinanceCalculator);
}
